import * as React from 'react';

interface UseBhanzuBuddyEffectsProps {
  chatContainerRef: React.RefObject<HTMLDivElement>;
  messages: { id: string }[];
  isOpen: boolean;
  hasScrolledOnOpen: React.RefObject<boolean>;
  message: string;
  prevMessageRef: React.RefObject<string>;
  handleToggleOpen: () => void;
  quickChallengeContextRef: React.RefObject<{
    questionByBB: string;
  } | null>;
  appendBuddyMessage?: (message: string) => void;
  isControlled: boolean;
  setChatState: React.Dispatch<
    React.SetStateAction<'closed' | 'opening' | 'open' | 'closing'>
  >;
}

export const useBhanzuBuddyEffects = ({
  chatContainerRef,
  messages,
  isOpen,
  hasScrolledOnOpen,
  message,
  prevMessageRef,
  handleToggleOpen,
  quickChallengeContextRef,
  appendBuddyMessage,
  isControlled,
  setChatState,
}: UseBhanzuBuddyEffectsProps) => {
  // Auto-scroll to bottom whenever messages change
  React.useEffect(() => {
    if (chatContainerRef.current && messages.length > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTo({
            top: chatContainerRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }
      });
    }
  }, [messages.length, chatContainerRef]);

  // Auto-scroll to bottom when chat opens for the first time
  React.useEffect(() => {
    if (
      isOpen &&
      chatContainerRef.current &&
      !hasScrolledOnOpen.current &&
      messages.length > 0
    ) {
      const timer = setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop =
            chatContainerRef.current.scrollHeight;
          hasScrolledOnOpen.current = true;
        }
      }, 450);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [isOpen, messages, chatContainerRef, hasScrolledOnOpen]);

  // Prevent background scrolling when chat is open
  React.useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ESC key support
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isOpen && event.key === 'Escape') {
        handleToggleOpen();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleToggleOpen]);

  // Sync chatState with controlled isOpen prop
  React.useEffect(() => {
    if (isControlled) {
      const targetState = isOpen ? 'open' : 'closed';
      setChatState(prevState => {
        if (prevState !== targetState) {
          return targetState;
        }
        return prevState;
      });
    }
  }, [isOpen, isControlled, setChatState]);

  // Auto-open BhanzuBuddy when text appears in textbox
  React.useEffect(() => {
    const currentMessage = message.trim();
    const prevMessage = prevMessageRef.current?.trim();
    if (currentMessage && currentMessage !== prevMessage && !isOpen) {
      handleToggleOpen();
      const pendingQC = quickChallengeContextRef.current;
      if (pendingQC && appendBuddyMessage) {
        setTimeout(() => {
          appendBuddyMessage(pendingQC.questionByBB);
        }, 100);
      }
    }
    prevMessageRef.current = message;
  }, [
    message,
    isOpen,
    handleToggleOpen,
    appendBuddyMessage,
    prevMessageRef,
    quickChallengeContextRef,
  ]);
};
